/**
 * Viral Social Sharing Configuration
 * Optimized copy for different platforms and content types
 * Focus on meme sounds and Tesla culture
 */

import { PUBLIC_SOUND_COUNT_LABEL } from './public-sound-count'

export interface ShareCopy {
  title: string;
  description: string;
  hashtags: string[];
  cta: string;
}

export interface PlatformConfig {
  maxLength: number;
  supportsHashtags: boolean;
  supportsMentions: boolean;
  supportsLinks: boolean;
}

// Platform configurations
export const PLATFORM_CONFIGS: Record<string, PlatformConfig> = {
  twitter: {
    maxLength: 280,
    supportsHashtags: true,
    supportsMentions: true,
    supportsLinks: true,
  },
  facebook: {
    maxLength: 63206, // Very high limit
    supportsHashtags: true,
    supportsMentions: true,
    supportsLinks: true,
  },
  linkedin: {
    maxLength: 3000,
    supportsHashtags: true,
    supportsMentions: false,
    supportsLinks: true,
  },
  reddit: {
    maxLength: 300,
    supportsHashtags: false,
    supportsMentions: true,
    supportsLinks: true,
  },
};

// ============================================
// VIRAL COPY LIBRARY
// ============================================

/**
 * Meme sound copy - Funny, trending sounds
 */
const memeSoundCopy: Record<string, ShareCopy[]> = {
  vine_boom: [
    {
      title: "💥 VINE BOOM as my Tesla lock sound",
      description: "When someone unlocks my Model 3 and the whole neighborhood knows it 🚗💥",
      hashtags: ["Tesla", "LockSound", "VineBoom", "Model3", "TeslaLife"],
      cta: "Download yours free →",
    },
    {
      title: "My Tesla goes 💥 BOOM",
      description: "Nothing says 'I'm here' quite like the Vine Boom echoing through the parking garage",
      hashtags: ["TeslaLockSound", "Vine", "Boom", "TeslaModelY"],
      cta: "Get the sound →",
    },
  ],
  emotional_damage: [
    {
      title: "My Tesla roasting me every time I unlock it 😭",
      description: "EMOTIONAL DAMAGE as a lock sound? Yes. Do I regret it? No.",
      hashtags: ["EmotionalDamage", "Tesla", "LockSound", "Funny"],
      cta: "Get roasted →",
    },
    {
      title: "When your Tesla knows you spent too much on mods 💸",
      description: "Steven He would be proud of this lock sound",
      hashtags: ["TeslaMod", "EmotionalDamage", "StevenHe"],
      cta: "Download free →",
    },
  ],
  rickroll: [
    {
      title: "🎵 Never gonna give you up... as my Tesla lock sound",
      description: "Rickrolling the entire parking lot every time I unlock my Model Y",
      hashtags: ["Rickroll", "Tesla", "NeverGonnaGiveYouUp", "LockSound"],
      cta: "Get rickrolled →",
    },
  ],
  among_us: [
    {
      title: "My Tesla is sus 🔪",
      description: "When your lock sound is the Among Us emergency meeting sound and everyone's looking around confused",
      hashtags: ["AmongUs", "Sus", "Tesla", "LockSound", "EmergencyMeeting"],
      cta: "Download →",
    },
  ],
  sad_trombone: [
    {
      title: "My Tesla when I forget to charge it 🎺📉",
      description: "*womp womp* - the saddest lock sound ever, perfect for my empty battery life",
      hashtags: ["SadTrombone", "Tesla", "EVLife", "WompWomp"],
      cta: "Feel the sadness →",
    },
  ],
  anime: [
    {
      title: "NANI?! My Tesla has ANIME LOCK SOUNDS?!",
      description: "When you unlock your Model 3 and it hits different in Japanese",
      hashtags: ["TeslaAnime", "LockSound", "Waifu", "Anime", "TeslaLife"],
      cta: "Download free →",
    },
    {
      title: "My Tesla speaks Japanese now 🇯🇵✨",
      description: "Anime girl lock sounds for my Model Y because why not",
      hashtags: ["Anime", "Tesla", "Japanese", "Kawaii", "LockSound"],
      cta: "Get yours →",
    },
  ],
};

/**
 * General Tesla culture copy
 */
const teslaCultureCopy: ShareCopy[] = [
  {
    title: "Finally made my Tesla interesting 🎵",
    description: "Stock lock sounds are so 2020. Upgraded my Model 3 with custom sounds and now everyone asks about it",
    hashtags: ["Tesla", "Model3", "Customization", "LockSound", "Upgrade"],
    cta: "Customize yours →",
  },
  {
    title: "My Tesla got a personality upgrade 🎧",
    description: `${PUBLIC_SOUND_COUNT_LABEL} sounds to choose from. Currently running the Vine Boom and the whole neighborhood knows when I'm home`,
    hashtags: ["TeslaLife", "VineBoom", "Customization", "LockSound"],
    cta: "Browse sounds →",
  },
  {
    title: "Tesla owner starter pack: ",
    description: "✅ FSD beta access\n✅ Custom lock sounds\n✅ Judging other EVs\n✅ Explaining it's not just 'a Prius'",
    hashtags: ["Tesla", "EVLife", "TeslaOwner", "LockSound"],
    cta: "Get the sounds →",
  },
];

/**
 * Specific sound category copy
 */
const categoryCopy: Record<string, ShareCopy[]> = {
  memes: [
    {
      title: "Tesla + Memes = Perfection 🎯",
      description: "Turn your Tesla into a walking meme with these custom lock sounds. Vine Boom? Emotional Damage? We got it all.",
      hashtags: ["TeslaMemes", "LockSound", "Viral", "TeslaLife"],
      cta: "Meme-ify your Tesla →",
    },
  ],
  anime: [
    {
      title: "Itasha? Cute. But have you tried anime LOCK SOUNDS? 🎌",
      description: "Your Tesla deserves to be as weeby as your taste in cars. Japanese voice lines for every unlock.",
      hashtags: ["TeslaAnime", "Itasha", "Weeb", "LockSound"],
      cta: "Download free →",
    },
  ],
  movies: [
    {
      title: "My Tesla is a movie star 🎬",
      description: "From Star Wars to Marvel - my Model 3 now has iconic movie quotes as lock sounds",
      hashtags: ["Tesla", "MovieQuotes", "StarWars", "LockSound"],
      cta: "Get cinematic →",
    },
  ],
  games: [
    {
      title: "My Tesla is a gamer 🎮",
      description: "Mario coin sounds for unlock, Minecraft EXP for lock. My Tesla understands my priorities.",
      hashtags: ["TeslaGamer", "Mario", "Minecraft", "LockSound"],
      cta: "Level up →",
    },
  ],
};

// ============================================
// COPY GENERATION FUNCTIONS
// ============================================

/**
 * Get share copy for a specific sound
 */
export function getSoundShareCopy(
  soundTitle: string,
  category: string,
  platform: 'twitter' | 'facebook' | 'linkedin' | 'reddit' | 'email'
): ShareCopy {
  const normalizedTitle = soundTitle.toLowerCase().replace(/[^\w]/g, '_');
  
  // Check for meme sound matches
  for (const [key, copies] of Object.entries(memeSoundCopy)) {
    // eslint-disable-next-line react-doctor/js-set-map-lookups -- String.includes, not Array.includes
    if (normalizedTitle.includes(key) || soundTitle.toLowerCase().includes(key.replace('_', ' '))) {
      // Pick random variation
      return copies[Math.floor(Math.random() * copies.length)];
    }
  }
  
  // Check for category matches
  const categoryKey = category.toLowerCase().replace(/\s+/g, '_');
  if (categoryCopy[categoryKey]) {
    return categoryCopy[categoryKey][0];
  }
  
  // Default copy
  return {
    title: `My Tesla now plays "${soundTitle}" when I unlock it 🔊`,
    description: `Found the perfect ${category} lock sound for my Tesla. 60 second install, totally worth it.`,
    hashtags: ["Tesla", "LockSound", "TeslaCustomization"],
    cta: "Download yours →",
  };
}

/**
 * Format copy for specific platform
 */
export function formatForPlatform(
  copy: ShareCopy,
  platform: 'twitter' | 'facebook' | 'linkedin' | 'reddit',
  url: string
): string {
  const config = PLATFORM_CONFIGS[platform];
  
  let formatted = copy.description;
  
  if (config.supportsHashtags && copy.hashtags.length > 0) {
    const hashtags = copy.hashtags.map(h => `#${h}`).join(' ');
    formatted += `\n\n${hashtags}`;
  }
  
  if (config.supportsLinks) {
    formatted += `\n\n${copy.cta} ${url}`;
  }
  
  // Trim if needed (Twitter)
  if (platform === 'twitter' && formatted.length > config.maxLength) {
    const maxDesc = config.maxLength - copy.cta.length - url.length - copy.hashtags.length * 15 - 10;
    formatted = copy.description.substring(0, maxDesc) + '...';
    if (config.supportsHashtags) {
      const hashtags = copy.hashtags.slice(0, 2).map(h => `#${h}`).join(' ');
      formatted += `\n\n${hashtags}`;
    }
    formatted += `\n\n${copy.cta} ${url}`;
  }
  
  return formatted;
}

/**
 * Get OG meta tags for a sound
 */
export function getSoundMetaTags(
  soundTitle: string,
  category: string,
  soundUrl: string,
  imageUrl?: string
): {
  title: string;
  description: string;
  image: string;
  url: string;
} {
  const copy = getSoundShareCopy(soundTitle, category, 'twitter');
  
  return {
    title: copy.title,
    description: copy.description,
    image: imageUrl || '/tesla-lock-sound-og.jpg',
    url: soundUrl,
  };
}

// ============================================
// TRACKING URL BUILDERS
// ============================================

/**
 * Build share URL with UTM parameters for tracking
 */
export function buildShareUrl(
  baseUrl: string,
  platform: string,
  contentType: string,
  contentId: string
): string {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', platform);
  url.searchParams.set('utm_medium', 'social');
  url.searchParams.set('utm_campaign', 'viral_share');
  url.searchParams.set('utm_content', `${contentType}_${contentId}`);
  return url.toString();
}

/**
 * The ONE utm scheme for owned share surfaces (header button, receipt,
 * share-card caption): utm_source=share, utm_medium=<surface>. Every owned
 * surface must use this so funnel attribution is comparable across surfaces
 * (previously: raw URLs, `?ref=share`, and per-surface utm all coexisted).
 */
export function buildSurfaceShareUrl(canonicalUrl: string, surface: string): string {
  const url = new URL(canonicalUrl);
  url.searchParams.set('utm_source', 'share');
  url.searchParams.set('utm_medium', surface);
  return url.toString();
}

/**
 * Canonical share-back text + pre-filled X/Reddit intent links for a sound.
 * Pass the slug as `soundPathId` when available — UUID paths hit a 308
 * redirect and unfurl as non-canonical.
 */
export function buildSoundShareBack(
  siteUrl: string,
  soundPathId: string,
  soundTitle: string,
  soundId: string
): { x: string; reddit: string; text: string } {
  const canonical = `${siteUrl}/sounds/${soundPathId}`;
  const text = `Just set "${soundTitle}" as my Tesla lock sound.`;
  const xUrl = buildShareUrl(canonical, 'x', 'sound', soundId);
  const redditUrl = buildShareUrl(canonical, 'reddit', 'sound', soundId);
  return {
    x: `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(xUrl)}`,
    reddit: `https://www.reddit.com/submit?url=${encodeURIComponent(redditUrl)}&title=${encodeURIComponent(text)}`,
    text,
  };
}

// ============================================
// VIRAL HOOKS
// ============================================

export const viralHooks = [
  "When your Tesla lock sound is cooler than your car 🎵",
  "My Tesla has more personality than I do 😅",
  "Just unlocked my Tesla and the whole parking lot applauded 👏",
  "POV: You unlocked your Tesla with a Vine Boom sound 💥",
  "Nobody:\nMy Tesla: *plays anime intro* 🎌",
  "I downloaded ONE lock sound and now I can't stop unlocking my Tesla 🤣",
];

/**
 * Get random viral hook
 */
export function getRandomViralHook(): string {
  return viralHooks[Math.floor(Math.random() * viralHooks.length)];
}
